import React, { useEffect, useState } from "react";
import { adminCompanyApiService } from "../../../../infrastructure/api/admin/admin-company-api-service";
import { adminMachineUseCase } from "../../../../di/container";
import type {
  CollectorStatus,
  CompanyEquipmentStatus,
} from "../../../../domain/types/admin-company";
import type { MachineDetailDto } from "../../../../domain/types/machine";

interface EquipmentStatusTabProps {
  companyId: number | null;
}

// ─── EquipmentStatus 본문 (대표 장비 + 메타 스냅샷 + 연결 상태) ──────────────
// 백엔드: GET /api/admin/company/{companyId}/equipment-status
// 응답: domain/types/admin-company.ts > CompanyEquipmentStatus
//
// 회사 KPI 보고 설정(인증키/kpiEnabled/kpiTestMode/kpiAutoReport)은
// 별도 컴포넌트 KpiReportSettingsSection 으로 분리되어 카테고리 탭
// (CategoryContent) 안에서 렌더된다. 본 탭은 장비 현황만 표시한다.

// ─── 포매터 ────────────────────────────────────────────────────────────────
const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

const formatNumber = (n: number): string => n.toLocaleString("ko-KR");

const formatHours = (hours: number | null): string => {
  if (hours === null) return "—";
  return `${formatNumber(Math.round(hours))} 시간`;
};

const formatPercent = (rate: number | null): string => {
  if (rate === null) return "—";
  return `${rate.toFixed(1)} %`;
};

// ─── 연결 상태 라벨/색상 매핑 ──────────────────────────────────────────────
const CONNECTION_LABEL: Record<
  CollectorStatus,
  { label: string; dotColor: string }
> = {
  NORMAL: { label: "정상", dotColor: "#27AE60" },
  WARNING: { label: "경고", dotColor: "#E0A800" },
  OFFLINE: { label: "오프라인", dotColor: "#F24A4A" },
  NOT_INSTALLED: { label: "미설치", dotColor: "#888888" },
};

// ─── 하단 안내 바 — 실제 연결 상태별 색/아이콘/문구 ──────────────────────────
const STATUS_BAR: Record<
  CollectorStatus,
  { bg: string; border: string; text: string; icon: string; message: string }
> = {
  NORMAL: {
    bg: "#E6F4EA",
    border: "rgba(39,174,96,0.3)",
    text: "#1F7A3F",
    icon: "✓",
    message: "현재 장비에서 수집된 데이터가 정상적으로 업데이트되고 있습니다.",
  },
  WARNING: {
    bg: "#FFF7E6",
    border: "rgba(224,168,0,0.35)",
    text: "#8A6D00",
    icon: "⚠",
    message: "데이터 수집이 지연되고 있습니다. 연결 상태를 확인하세요.",
  },
  OFFLINE: {
    bg: "#FDECEC",
    border: "rgba(242,74,74,0.3)",
    text: "#B23B3B",
    icon: "⚠",
    message: "장비가 오프라인 상태입니다. 데이터 수집이 중단되었습니다.",
  },
  NOT_INSTALLED: {
    bg: "#F5F5F5",
    border: "#E0E0E0",
    text: "#666666",
    icon: "ℹ",
    message: "데이터 수집기가 설치되지 않았습니다.",
  },
};

const EquipmentStatusTab: React.FC<EquipmentStatusTabProps> = ({ companyId }) => {
  const [data, setData] = useState<CompanyEquipmentStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 장비 선택 (정보 보기용 — 저장 아님). null = 대표 장비
  const [machines, setMachines] = useState<MachineDetailDto[]>([]);
  const [selectedMachineId, setSelectedMachineId] = useState<number | null>(
    null,
  );

  // 회사 장비 목록(드롭다운) + 회사 바뀌면 선택 초기화(대표 장비)
  useEffect(() => {
    setSelectedMachineId(null);
    if (companyId === null || Number.isNaN(companyId)) {
      setMachines([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const list = await adminMachineUseCase.getMachines(companyId);
        if (!cancelled) setMachines(list);
      } catch (e) {
        if (!cancelled) console.error("장비 목록 조회 실패:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (companyId === null || Number.isNaN(companyId)) {
      setData(null);
      setIsLoading(false);
      setErrorMessage(null);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setErrorMessage(null);
    (async () => {
      try {
        const response =
          await adminCompanyApiService.getCompanyEquipmentStatus(
            companyId,
            selectedMachineId ?? undefined,
          );
        if (cancelled) return;
        setData(response.data ?? null);
      } catch (error) {
        if (cancelled) return;
        const message =
          error instanceof Error
            ? error.message
            : "EquipmentStatus 정보를 가져오지 못했습니다.";
        console.error("EquipmentStatus 조회 실패:", error);
        setErrorMessage(message);
        setData(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, selectedMachineId]);

  const equipment = data?.representativeEquipment ?? null;
  const snapshot = data?.metaSnapshot ?? null;
  const connection = data?.connectionSummary ?? null;
  const connectionLabel = connection
    ? CONNECTION_LABEL[connection.connectionStatus]
    : null;

  const metaRows: { label: string; value: string }[] = snapshot
    ? [
        { label: "총 출력 매수", value: `${formatNumber(snapshot.totalWorkloadCount)} 장` },
        { label: "누적 가동 시간", value: formatHours(snapshot.cumulativeRuntimeHours) },
        { label: "평균 가동률", value: formatPercent(snapshot.averageUtilizationRate) },
      ]
    : [];
  const snapshotCapturedAt = formatDateTime(snapshot?.capturedAt);

  return (
    <div className="p-5">
      {isLoading && (
        <div className="mb-4 text-[12px] text-[#888]">불러오는 중…</div>
      )}
      {errorMessage && (
        <div
          className="mb-4 px-3 py-2 rounded-md text-[12px]"
          style={{
            background: "#EBF3FF",
            color: "#4285F4",
            border: "1px solid rgba(66,133,244,0.2)",
          }}
        >
          {errorMessage}
        </div>
      )}

      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-[14px] font-semibold mb-1">장비 정보</h3>
          <p className="text-[12px] text-[#888]">
            장비를 선택해 메타데이터·연결 상태를 확인합니다. (KPI 보고 장비 지정은 카테고리 탭에서)
          </p>
        </div>
        <select
          value={selectedMachineId ?? ""}
          onChange={(e) =>
            setSelectedMachineId(
              e.target.value === "" ? null : Number(e.target.value),
            )
          }
          className="border border-[#D9D9D9] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#4285F4] bg-white min-w-[220px]"
        >
          <option value="">대표 장비</option>
          {machines.map((m) => (
            <option key={m.machineId} value={m.machineId}>
              {m.manufacturer} {m.modelName} ({m.ipAddress})
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* 좌측 장비 일러스트 */}
        <div className="col-span-12 md:col-span-3 border border-[#D9D9D9] rounded-[10px] overflow-hidden">
          <div
            className="h-[180px] text-[70px] flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg,#EDEEF2 0%, #F7F8FA 100%)",
            }}
          >
            🖨️
          </div>
          <div className="p-3.5 border-t border-[#D9D9D9]">
            <div className="text-[14px] font-semibold">
              {equipment?.modelName ?? "—"}
            </div>
            <div className="text-[11px] text-[#888] mt-0.5">
              {equipment?.modelSubtitle ?? "장비 정보 없음"}
            </div>
          </div>
        </div>

        {/* 우측 4 카드 + 메타데이터 */}
        <div className="col-span-12 md:col-span-9 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="border border-[#D9D9D9] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">연결 상태</div>
              <div className="flex items-center justify-center gap-1.5 mt-0.5">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: connectionLabel?.dotColor ?? "#BBBBBB" }}
                />
                <span className="text-[13px] font-semibold">
                  {connectionLabel?.label ?? "—"}
                </span>
              </div>
            </div>
            <div className="border border-[#D9D9D9] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">최근 수집 시간</div>
              <div className="text-[13px] font-semibold">
                {formatDateTime(connection?.lastCollectedAt)}
              </div>
            </div>
            <div className="border border-[#D9D9D9] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">연결 방식</div>
              <div className="text-[13px] font-semibold">
                {connection?.connectionMethod ?? "—"}
              </div>
            </div>
            <div className="border border-[#D9D9D9] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">최근 KPI 전송</div>
              <div className="text-[13px] font-semibold">
                {formatDateTime(connection?.lastKpiTransmittedAt)}
              </div>
            </div>
          </div>

          {/* 메타데이터 스냅샷 */}
          <div className="rounded-[10px] overflow-hidden border border-[#D9D9D9]">
            <div className="flex items-center px-4 py-3 border-b border-[#D9D9D9] bg-[#F8F8F8]">
              <h4 className="text-[13px] font-semibold">장비 메타데이터 (스냅샷)</h4>
            </div>
            <table className="w-full">
              <tbody>
                {metaRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-[#888] text-[13px] py-[14px] px-[18px] text-center"
                    >
                      장비 메타데이터가 없습니다.
                    </td>
                  </tr>
                ) : (
                  metaRows.map((meta, idx) => (
                    <tr key={meta.label}>
                      <td
                        className={`w-[240px] text-[#888] text-[13px] py-[11.5px] px-[18px] ${
                          idx < metaRows.length - 1
                            ? "border-b border-[#D9D9D9]"
                            : ""
                        }`}
                      >
                        {meta.label}
                      </td>
                      <td
                        className={`font-medium text-[13px] py-[11.5px] px-[18px] ${
                          idx < metaRows.length - 1
                            ? "border-b border-[#D9D9D9]"
                            : ""
                        }`}
                      >
                        {meta.value}
                      </td>
                      <td
                        className={`text-[#888] text-[13px] text-right py-[11.5px] px-[18px] ${
                          idx < metaRows.length - 1
                            ? "border-b border-[#D9D9D9]"
                            : ""
                        }`}
                      >
                        {snapshotCapturedAt}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* 하단 안내 바 */}
      {connection &&
        (() => {
          const bar = STATUS_BAR[connection.connectionStatus];
          return (
            <div
              className="mt-5 rounded-md px-4 py-3 flex items-center border"
              style={{ background: bar.bg, borderColor: bar.border }}
            >
              <div
                className="flex items-center gap-2 text-[12.5px]"
                style={{ color: bar.text }}
              >
                <span>{bar.icon}</span>
                <span>{bar.message}</span>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default EquipmentStatusTab;
